import React, { useEffect, useState, useRef } from 'react';
import gMapsIcon from "assets/svg/gmaps_icon.svg";
import listService from 'services/list.service';

const List = ({ list }) => {
    const { title, nElements, nViews, nUpvotes, category, link, createdAt, city, country, source } = list;
    const [dateText, setDateText] = useState(null);
    const [currentList, setCurrentList] = useState(list);

    let iconClass = '';
    let colorClass = '';

    // Determine the icon and color based on category
    switch (currentList.category) {
        case 'food':
            iconClass = 'fas fa-utensils';
            colorClass = 'bg-yellow-200 text-yellow-600';
            break;
        case 'hotel':
            iconClass = 'fas fa-bed';
            colorClass = 'bg-emerald-200 text-emerald-600';
            break;
        case 'guide':
            iconClass = 'fas fa-camera';
            colorClass = 'bg-purple-200 text-purple-600';
            break;
        default:
            break;
    }

    useEffect(() => {
        const oneHourInMillis = 60 * 60 * 1000; // number of milliseconds in an hour
        const oneDayInMillis = 24 * oneHourInMillis; // number of milliseconds in a day
        const oneWeekInMillis = 7 * oneDayInMillis; // number of milliseconds in a week
        const oneMonthInMillis = 30 * oneDayInMillis; // number of milliseconds in a month
        const oneYearInMillis = 365 * oneDayInMillis; // number of milliseconds in a year
        const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

        const date = new Date(currentList.createdAt);
        const now = new Date(); // get the current date and time

        // display the dates like this:
        // if less than a hour, diplay 4m ago
        // if less than a day, display 4h ago
        // if less than a week, display 4d ago
        // if less than a year, display 4mo ago
        // if more than a year, display 4y ago
        const diff = now - date;
        if (diff < oneHourInMillis) {
            setDateText(`${Math.floor(diff / (60 * 1000))}m ago`);
        } else if (diff < oneDayInMillis) {
            setDateText(`${Math.floor(diff / (60 * 60 * 1000))}h ago`);
        } else if (diff < oneMonthInMillis) {
            setDateText(`${Math.floor(diff / (24 * 60 * 60 * 1000))}d ago`);
        } else if (diff < oneYearInMillis) {
            setDateText(`${Math.floor(diff / (30 * 24 * 60 * 60 * 1000))}mo ago`);
        } else {
            setDateText(`${Math.floor(diff / (365 * 30 * 24 * 60 * 60 * 1000))}mo ago`);
        }
    }, [createdAt]);

    function upvote(isUpvote) {
        const updatedList = { ...currentList };
        if (isUpvote) {
            updatedList.nUpvotes += 1;
        } else {
            updatedList.nUpvotes -= 1;
        }
        setCurrentList(updatedList);
        listService.upvoteList(list._id, isUpvote);
    }

    return (
        <>
            <section className="relative py-16">
                <div className="container mx-auto px-4 mt-32">
                    <div className="relative flex flex-col min-w-0 break-words bg-white w-full mb-6 shadow-xl rounded-lg -mt-64 mb-8">
                        <div className="md:px-6">
                            <div className="py-4 border-blueGray-200 text-center flex flex-col md:flex-row flex-wrap">
                                <div className="w-full md:w-8/12 border-t-0 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-left flex flex-col md:flex-row items-center md:items-baseline">
                                    <p className="text-left text-xl font-bold leading-relaxed text-blueGray-700">
                                        {currentList.title}
                                    </p>
                                    <p className="text-left text-base leading-relaxed text-blueGray-600 ml-0 md:ml-4">
                                        {currentList.nElements} places
                                    </p>
                                </div>
                                <div className="w-full md:w-4/12 self-center flex justify-center md:justify-end items-center px-4 text-sm leading-normal text-blueGray-400 font-bold">
                                    <a
                                        href={currentList.link}
                                        target='_blank'
                                        rel='noreferrer'
                                        className="flex flex-row text-primary font-bold px-6 py-4 rounded outline-none focus:outline-none bg-trabsparent border border-solid border-primary uppercase text-sm shadow hover:shadow-lg ease-linear transition-all duration-150"
                                    >
                                        <img src={gMapsIcon} className="h-6 w-6 mr-2" alt="Google Maps" />
                                        Add to Google Maps
                                    </a>
                                </div>
                                <div className='pt-6 flex flex-col-reverse md:flex-row w-full'>
                                    <div className="mt-4 md:mt-0 w-full md:w-6/12 border-t-0 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap text-left justify-center md:justify-start flex items-center">
                                        <div className="flex flex-row ml-3 items-center">
                                            <button
                                                onClick={() => upvote(true)}
                                                className="text-xl font-bold text-primary"
                                            >
                                                <i className="fas fa-arrow-up"></i>
                                            </button>
                                            <p className="text-base text-primary mx-2">
                                                {currentList.nUpvotes}
                                            </p>
                                            <button
                                                onClick={() => upvote(false)}
                                                className="text-xl font-bold text-primary"
                                            >
                                                <i className="fas fa-arrow-down "></i>
                                            </button>
                                            <i className="ml-4 text-sm fas fa-eye text-blueGray-500 font-bold"></i>
                                            <p className="text-base ml-1 text-blueGray-500">
                                                {currentList.nViews}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="w-full md:w-6/12 self-center flex justify-center md:justify-end items-center text-sm leading-normal text-blueGray-400 font-bold">
                                        <i className="fas fa-location-dot mr-2 text-lg"></i>{" "}
                                        {`${currentList.city ? currentList.city : `${currentList.country ? currentList.country : ''}`}`}
                                        {/* 
                                        <span className={`ml-4 text-xs font-semibold inline-block py-1 px-2 uppercase rounded ${colorClass}`}>
                                            <i className={`${iconClass} text-xs mr-1`}></i> {category}
                                        </span>
                                        */}
                                        <i className="far fa-calendar ml-4 mr-2 text-lg text-blueGray-400"></i>{" "}
                                        {dateText}
                                        <a
                                            href={currentList.source}
                                            target='_blank'
                                            rel='noreferrer'
                                            className='ml-4'
                                        >
                                            Source
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    )
};

export default List;